import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, ChevronDown, ChevronUp, Clock, CheckCircle,
  Truck, XCircle, ExternalLink, MapPin, ShoppingBag, ArrowLeft,
  Star, EyeOff, Send, RotateCcw,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { orders as ordersApi, reviews as reviewsApi, type OrderAPI } from '../api/client';
import SEO from '../components/SEO';
import { pageSeoMeta } from '@shared/pageSeoMeta';

const STATUS_CONFIG: Record<string, { bg: string; text: string; border: string; icon: React.ElementType; label: string }> = {
  pending:   { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/30', icon: Clock,       label: 'Pending' },
  confirmed: { bg: 'bg-blue-500/10',   text: 'text-blue-500',   border: 'border-blue-500/30',   icon: CheckCircle, label: 'Confirmed' },
  shipped:   { bg: 'bg-purple-500/10',  text: 'text-purple-500', border: 'border-purple-500/30',  icon: Truck,       label: 'Shipped' },
  delivered: { bg: 'bg-emerald-500/10', text: 'text-emerald-500',border: 'border-emerald-500/30', icon: CheckCircle, label: 'Delivered' },
  cancelled: { bg: 'bg-red-500/10',     text: 'text-red-500',    border: 'border-red-500/30',     icon: XCircle,     label: 'Cancelled' },
  returned:  { bg: 'bg-orange-500/10',  text: 'text-orange-500', border: 'border-orange-500/30',  icon: RotateCcw,   label: 'Returned' },
};

const STEP_ORDER = ['pending', 'confirmed', 'shipped', 'delivered'];

function StatusStepper({ status, isDark }: { status: string; isDark: boolean }) {
  if (status === 'cancelled' || status === 'returned') return null;
  const currentIdx = STEP_ORDER.indexOf(status);
  return (
    <div className="flex items-center gap-1 w-full mt-3">
      {STEP_ORDER.map((step, idx) => {
        const done = idx <= currentIdx;
        const cfg = STATUS_CONFIG[step];
        const Icon = cfg.icon;
        return (
          <div key={step} className="flex items-center flex-1 last:flex-initial">
            <div className={`flex flex-col items-center gap-1 ${done ? cfg.text : isDark ? 'text-gray-600' : 'text-gray-300'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all ${done ? `${cfg.border} ${cfg.bg}` : isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <Icon size={13} />
              </div>
              <span className="text-[10px] font-medium whitespace-nowrap">{cfg.label}</span>
            </div>
            {idx < STEP_ORDER.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 rounded-full mb-5 ${idx < currentIdx ? 'bg-emerald-500' : isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function MyOrdersPage() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myOrders, setMyOrders] = useState<OrderAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Review state per order+product
  const [reviewForms, setReviewForms] = useState<Record<string, { rating: number; text: string; isAnonymous: boolean }>>({});
  const [reviewSubmitting, setReviewSubmitting] = useState<string | null>(null);
  const [reviewedItems, setReviewedItems] = useState<Set<string>>(new Set());
  const [reviewErrors, setReviewErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    ordersApi.list()
      .then(setMyOrders)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const fmt = (n: number) => `\u20B9${n?.toLocaleString('en-IN') ?? 0}`;

  const getReviewKey = (orderId: string, productId: string) => `${orderId}__${productId}`;

  const getForm = (key: string) => reviewForms[key] || { rating: 5, text: '', isAnonymous: false };

  const updateForm = (key: string, field: string, value: unknown) => {
    setReviewForms(prev => ({ ...prev, [key]: { ...getForm(key), [field]: value } }));
  };

  const submitReview = async (orderId: string, productId: string) => {
    const key = getReviewKey(orderId, productId);
    const form = getForm(key);
    if (!form.text.trim()) {
      setReviewErrors(prev => ({ ...prev, [key]: 'Please write a review' }));
      return;
    }
    setReviewSubmitting(key);
    setReviewErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
    try {
      await reviewsApi.create({
        product_id: productId,
        order_id: orderId,
        rating: form.rating,
        text: form.text.trim(),
        is_anonymous: form.isAnonymous,
      });
      setReviewedItems(prev => new Set(prev).add(key));
      setReviewForms(prev => { const n = { ...prev }; delete n[key]; return n; });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to submit review';
      setReviewErrors(prev => ({ ...prev, [key]: msg }));
    } finally {
      setReviewSubmitting(null);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <SEO {...pageSeoMeta.orders} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate(-1)}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <ShoppingBag size={28} className="text-emerald-500" /> My Orders
            </h1>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {myOrders.length} order{myOrders.length !== 1 ? 's' : ''} placed
            </p>
          </div>
        </div>

        {/* Empty state */}
        {myOrders.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className={`text-center py-20 rounded-2xl border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}
          >
            <Package size={64} className={`mx-auto mb-4 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />
            <h2 className="text-xl font-semibold mb-2">No orders yet</h2>
            <p className={`text-sm mb-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Start shopping to see your orders here!</p>
            <Link to="/store"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold transition-colors">
              <ShoppingBag size={18} /> Browse Store
            </Link>
          </motion.div>
        )}

        {/* Orders list */}
        <div className="space-y-4">
          {myOrders.map((order, idx) => {
            const cfg = STATUS_CONFIG[order.status] ?? STATUS_CONFIG.pending;
            const StatusIcon = cfg.icon;
            const isExpanded = expandedId === order.id;
            const orderDate = new Date(order.created_at);
            const hasTracking = order.tracking_id && order.tracking_url;

            return (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`rounded-2xl border overflow-hidden transition-colors ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}
              >
                {/* Order header — always visible */}
                <div
                  className={`px-4 sm:px-6 py-4 cursor-pointer transition-colors ${isDark ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50'}`}
                  onClick={() => setExpandedId(isExpanded ? null : order.id)}
                >
                  <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          #{order.id.slice(0, 8)}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                          <StatusIcon size={12} /> {cfg.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        <p className="font-bold text-lg">{fmt(order.total)}</p>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {orderDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {' \u00B7 '}
                          {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Mini product preview when collapsed */}
                      {!isExpanded && (
                        <p className={`text-xs mt-1.5 truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {order.items.map(i => i.product_name).join(', ')}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Track button */}
                      {hasTracking && (
                        <a
                          href={`${order.tracking_url}${order.tracking_url!.includes('?') ? '&' : '?'}tracking_id=${encodeURIComponent(order.tracking_id!)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white text-xs font-semibold rounded-lg transition-colors"
                        >
                          <Truck size={14} /> Track Order <ExternalLink size={12} />
                        </a>
                      )}
                      <div className={`p-1 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </div>
                    </div>
                  </div>

                  {/* Status stepper */}
                  <StatusStepper status={order.status} isDark={isDark} />
                </div>

                {/* Expanded details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className={`px-4 sm:px-6 pb-5 pt-2 border-t space-y-5 ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                        {/* Items list */}
                        <div>
                          <p className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            <Package size={13} /> Items
                          </p>
                          <div className="space-y-2">
                            {order.items.map(item => (
                              <div key={item.id}
                                className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-sm truncate">{item.product_name}</p>
                                  <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    Qty: {item.quantity} {'\u00D7'} {fmt(item.unit_price)}
                                  </p>
                                </div>
                                <p className="font-semibold text-sm ml-4">{fmt(item.unit_price * item.quantity)}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          {/* Price breakdown */}
                          <div>
                            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              Price Summary
                            </p>
                            <div className={`space-y-2 text-sm p-3 rounded-xl ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                              <div className="flex justify-between">
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Subtotal</span>
                                <span>{fmt(order.total - order.shipping + (order.discount_amount ?? 0))}</span>
                              </div>
                              {order.discount_amount && order.discount_amount > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-emerald-500 flex items-center gap-1">
                                    Coupon{order.coupon_code ? ` (${order.coupon_code})` : ''}
                                  </span>
                                  <span className="text-emerald-500">-{fmt(order.discount_amount)}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Shipping</span>
                                <span>{order.shipping === 0 ? 'FREE' : fmt(order.shipping)}</span>
                              </div>
                              <div className={`flex justify-between font-bold pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                <span>Total</span>
                                <span className="text-emerald-500">{fmt(order.total)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Shipping address */}
                          <div>
                            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              <MapPin size={13} /> Shipping Address
                            </p>
                            <div className={`text-sm space-y-1 p-3 rounded-xl ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                              <p className="font-medium">{order.ship_first_name} {order.ship_last_name}</p>
                              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>{order.ship_address}</p>
                              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                                {order.ship_city}, {order.ship_state} - {order.ship_pincode}
                              </p>
                              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>{order.ship_phone}</p>
                            </div>
                          </div>
                        </div>

                        {/* Tracking info */}
                        {hasTracking && (
                          <div className={`p-4 rounded-xl border ${isDark ? 'bg-purple-500/5 border-purple-500/20' : 'bg-purple-50 border-purple-200'}`}>
                            <p className={`text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                              <Truck size={13} /> Tracking Information
                            </p>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div>
                                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                  Tracking ID: <span className="font-mono font-semibold">{order.tracking_id}</span>
                                </p>
                              </div>
                              <a
                                href={`${order.tracking_url}${order.tracking_url!.includes('?') ? '&' : '?'}tracking_id=${encodeURIComponent(order.tracking_id!)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm font-semibold rounded-lg transition-colors"
                              >
                                <Truck size={15} /> Track on Partner Website <ExternalLink size={13} />
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Review section — only for delivered orders */}
                        {order.status === 'delivered' && (
                          <div>
                            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              <Star size={13} /> Rate Your Purchase
                            </p>
                            <div className="space-y-3">
                              {order.items.map(item => {
                                if (!item.product_id) return null;
                                const key = getReviewKey(order.id, item.product_id);
                                const isReviewed = reviewedItems.has(key);
                                const form = getForm(key);
                                const isSubmitting = reviewSubmitting === key;
                                const error = reviewErrors[key];

                                return (
                                  <div key={key} className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800/30 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                                    <p className="font-medium text-sm mb-2">{item.product_name}</p>

                                    {isReviewed ? (
                                      <div className="flex items-center gap-2 text-emerald-500 text-sm">
                                        <CheckCircle size={16} /> Review submitted! Thank you.
                                      </div>
                                    ) : (
                                      <div className="space-y-3">
                                        {/* Star rating */}
                                        <div className="flex items-center gap-1">
                                          {[1, 2, 3, 4, 5].map(star => (
                                            <button
                                              key={star}
                                              type="button"
                                              onClick={() => updateForm(key, 'rating', star)}
                                              className="transition-transform hover:scale-110"
                                            >
                                              <Star
                                                size={22}
                                                fill={star <= form.rating ? '#f59e0b' : 'transparent'}
                                                stroke={star <= form.rating ? '#f59e0b' : isDark ? '#4b5563' : '#d1d5db'}
                                              />
                                            </button>
                                          ))}
                                          <span className={`text-xs ml-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{form.rating}/5</span>
                                        </div>

                                        {/* Review text */}
                                        <textarea
                                          placeholder="Share your experience with this product..."
                                          value={form.text}
                                          onChange={e => updateForm(key, 'text', e.target.value)}
                                          rows={3}
                                          className={`w-full px-3 py-2 rounded-lg border text-sm resize-none outline-none transition-colors ${isDark ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-600 focus:border-emerald-500' : 'bg-white border-gray-200 placeholder-gray-400 focus:border-emerald-500'}`}
                                        />

                                        {error && <p className="text-red-500 text-xs">{error}</p>}

                                        <div className="flex items-center justify-between gap-3 flex-wrap">
                                          {/* Anonymous toggle */}
                                          <label className={`flex items-center gap-2 cursor-pointer text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            <input
                                              type="checkbox"
                                              checked={form.isAnonymous}
                                              onChange={e => updateForm(key, 'isAnonymous', e.target.checked)}
                                              className="w-4 h-4 rounded accent-emerald-500"
                                            />
                                            <EyeOff size={13} /> Post anonymously
                                          </label>

                                          <button
                                            onClick={() => submitReview(order.id, item.product_id!)}
                                            disabled={isSubmitting || !form.text.trim()}
                                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition-colors"
                                          >
                                            {isSubmitting ? (
                                              <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                                            ) : (
                                              <Send size={13} />
                                            )}
                                            {isSubmitting ? 'Submitting...' : 'Submit Review'}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
